import React, { useCallback, useEffect, useRef, useState } from 'react'
import classnames from 'classnames'
import GlassSystem from './store'
import './style.scss'

type GlassRootTag = 'header' | 'footer' | 'div'

interface GlassProps {
  rootTag?: GlassRootTag
  className?: string
  contentClassName?: string
  id?: string
  classic?: boolean
  style?: React.CSSProperties
}

interface Vector2 {
  x: number
  y: number
}

const ClassicGlass: React.FC<GlassProps> = ({
  rootTag,
  className,
  contentClassName,
  id,
  style = {},
  children,
}) => {
  const RootElement = rootTag ?? 'div'

  return (
    <RootElement
      className={classnames('ez-glass-bg classic', className)}
      id={id}
      style={style}
    >
      <div className={classnames('ez-glass-content', contentClassName)}>
        {children}
      </div>
    </RootElement>
  )
}

const EffectiveGlass: React.FC<GlassProps> = ({
  rootTag,
  className,
  contentClassName,
  id,
  style = {},
  children,
}) => {
  const { x, y } = GlassSystem.useContainer()
  const domRef = useRef<HTMLDivElement>(null)
  const [domPostion, setDomPosition] = useState<Vector2>({ x: 0, y: 0 })
  const RootElement = rootTag ?? 'div'

  const handleScroll = useCallback(() => {
    if (domRef.current) {
      const rect = domRef.current.getBoundingClientRect()
      setDomPosition(rect)
    }
  }, [])

  useEffect(() => {
    handleScroll()
    window.addEventListener('scroll', handleScroll, { passive: false })
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [handleScroll])

  const xyStyle = { '--x': x - domPostion.x, '--y': y - domPostion.y, ...style }

  return (
    <RootElement
      ref={domRef}
      className={classnames('ez-glass-bg', className)}
      id={id}
      style={xyStyle as any}
    >
      <div className={classnames('ez-glass-content', contentClassName)}>
        {children}
      </div>
    </RootElement>
  )
}

const Glass: React.FC<GlassProps> = ({ classic = false, ...rest }) => {
  if (classic) {
    return <ClassicGlass {...rest} />
  }
  return <EffectiveGlass {...rest} />
}

export default Glass
